<template>
  <div>
    <div class="dashboard-page-title">
      <div class="container-fluid">
          <h1>Manager List</h1>
      </div>
    </div>

    <div class="container-fluid">
      <div class="dashboard-results-details">
        <div class="row">
          <div class="col-xs-12 col-sm-4">
              <router-link :to="{ name: 'managers.create' }" class="btn btn-sm btn btn-primary"><i class="fa fa-plus"></i> Add Manager</router-link>
          </div>
          <div class="col-xs-12 col-sm-8">
            <div class="form-inline m-b-sm">
              <form>
                <div class="form-group form-group-sort">
                  <div class="dropdown">
                    <button class="btn btn-block btn-default dropdown-toggle" type="button" id="sort-dropdown" data-toggle="dropdown" aria-haspopup="true">
                      {{ sortAndOrder.label }}
                    </button>
                    <ul class="dropdown-menu" aria-labelledby="sort-dropdown">
                      <li v-for="(option, index) in sortAndOrderOptions" :key="index">
                        <router-link
                          :to="{ query: routerLinkQueryForOption(option) }"
                          @click.native="sortAndOrder = option">
                          {{ option.label }}
                        </router-link>
                      </li>
                    </ul>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <user-list-filters
        class="margin-y-1"
        :set-filter-mutation="setFilter"
        :filters="filters"
        :apply-filters="handleApplyFiltersCallback">
        <ladda-button
          slot="button"
          :click="handleApplyFiltersCallback"
          type="submit"
          class="btn btn-primary pull-right">
          Search
        </ladda-button>
      </user-list-filters>

      <div class="dashboard-block">
        <ul class="nav nav-tabs" id="myTabs" role="tablist">
          <li :class="classForStatus('all')">
            <router-link :to="{ params: { status: 'all' }, query: $route.query }">All ({{ managersCounts.all || 0 }})</router-link>
          </li>
          <li :class="classForStatus('active')">
            <router-link :to="{ params: { status: 'active' }, query: $route.query }">Active ({{ managersCounts.active || 0 }})</router-link>
          </li>
          <li :class="classForStatus('unverified')">
            <router-link :to="{ params: { status: 'unverified' }, query: $route.query }">Unverified ({{ managersCounts.unverified || 0 }})</router-link>
          </li>
          <li :class="classForStatus('trash')">
            <router-link :to="{ params: { status: 'trash' }, query: $route.query }"><i class="fa fa-trash"></i></router-link>
          </li>
          <li class="ml-auto text-right" v-if="$route.params.status==='unverified' && (!filters.keywords)">
            <ladda-button
              slot="button"
              :click="handleSendActivationEmail"
              type="submit"
              class="btn btn-primary">
              Send Activation Email
            </ladda-button>
            <span class="help-block">This will notify all users who have not responded within the 24 hour period.</span>
          </li>
        </ul>
        <div>

        </div>

        <pickstar-loading v-if="fetchingManagers" class="m-t-lg"/>

        <no-results v-else-if="haveNoManagers">
          There are no managers.
        </no-results>

        <ul class="item-detail item-detail-profile" v-else>
          <manager-list-item
            v-for="manager in managers"
            :key="manager.id"
            :manager="manager"/>
        </ul>

        <lazy-loading
          class="text-center"
          :loading="loading"
          :reached-bottom="handleGetManagersForNextPageCallback"
          ref="lazyLoading">
          <pickstar-loading slot="loading"/>
          <span slot="finished"></span>
        </lazy-loading>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { appliesFilters, handleServerErrors } from 'mixins'
import { assign, find } from 'lodash'
import ManagerListItem from './ManagerListItem'
import LazyLoading from 'codium-lazy-loading'
import UserListFilters from '../UserListFilters'
import {notify} from 'utils'

export default {
  mixins: [appliesFilters, handleServerErrors],

  props: {
    status: {
      required: false,
      default: 'all'
    }
  },

  components: {
    UserListFilters,
    ManagerListItem,
    LazyLoading
  },

  data () {
    return {
      name: null,
      fetchingManagers: false,
      searchPromise: null,
      sort: 'name',
      order: 'asc',
      sortAndOrderOptions: [
        {
          label: 'Newest First',
          sort: 'date',
          order: 'desc'
        },
        {
          label: 'Oldest First',
          sort: 'date',
          order: 'asc'
        },
        {
          label: 'A-Z',
          sort: 'name',
          order: 'asc'
        },
        {
          label: 'Z-A',
          sort: 'name',
          order: 'desc'
        }
      ]
    }
  },

  computed: {
    ...mapState('manager', ['managers', 'managersCounts']),
    ...mapState('user', ['filters']),
    ...mapState('app', ['loading']),

    sortAndOrder: {
      get () {
        return find(this.sortAndOrderOptions, (option) => {
          return option.sort === this.sort &&
            option.order === this.order
        })
      },

      set ({ sort, order }) {
        this.sort = sort
        this.order = order
      }
    },

    haveNoManagers () {
      return this.managers.length === 0
    },

    localQueryParams () {
      let params = {
        ...this.queryParams,
        status: this.status,
        sort: this.sort,
        order: this.order
      }

      return params
    },

    haveNoTalent () {
      return this.talents.length === 0
    }
  },

  watch: {
    '$route' () {
      this.resetManagers()

      this.fetchingManagers = true

      this.getManagers(this.localQueryParams)
        .then(() => {
          this.$nextTick(() => {
            if (!this.$refs.lazyLoading) {
              return
            }

            this.$refs.lazyLoading.$emit('reset')
          })
        })
        .catch(this.handleServerErrorsCallback)
        .finally(() => {
          this.fetchingManagers = false

          if (this.searchPromise) {
            this.searchPromise.resolve()

            this.searchPromise = null
          }
        })

      this.getManagerCounts(this.localQueryParams)
        .catch(this.handleServerErrorsCallback)
    }
  },

  created () {
    this.resetManagers()
  },

  methods: {
    ...mapActions('manager', ['resetManagers', 'getManagers', 'getManagersForNextPage', 'getManagerCounts', 'resetManagers']),
    ...mapActions('user', ['setFilter', 'resetFilters', 'sendActivationEmail']),

    handleGetManagersForNextPageCallback () {
      this.getManagerCounts(this.localQueryParams)
      return this.getManagersForNextPage(this.localQueryParams)
    },

    routerLinkQueryForOption (option) {
      return assign({}, this.$route.query, {
        sort: option.sort,
        order: option.order
      })
    },

    classForStatus (status) {
      if (this.status === status) {
        return 'active'
      }

      return ''
    },

    handleSendActivationEmail () {
      return this.sendActivationEmail('manager').then((response) => {
        notify.success('Email Activation sent to all ‘Unverified users’.')
      }).catch((error) => {
        notify.error('Error occurred.')
        return Promise.reject(error)
      })
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard-results-details .form-inline .dropdown {
  margin-bottom: 0px;
}
</style>
